/**
 * Pre-pour's hold, release, change and time move for a real patient go through nursing's keyon endpoints
 * (#336): HoldMedication, ClearCurrentMedicationHold, ChangeOrderForMedLogTimes and
 * InsertUpdateMedLogTimes. These build the bodies those endpoints take - the same fields nursing's Medication
 * Administration sends - and read keyon's holds and History back into the shapes the page draws.
 *
 * A pre-pour medication `m` carries its GetPatientMeds record as `m.keyon`, so a change order can repeat the
 * order's details (prescriber, pharmacy, route, diagnosis …) as they are; keyon builds the new row from them.
 * Every request names the row (medentryid), so keyon never picks a same-name medication.
 *
 * Times: the sheet's hours ("8am") are the medication's administration times - Administer already records
 * doses at the pour's hour (keyon/lib/PrePourAdminister.php) - so a hold of chosen hours and a time move send
 * those hours. A raised frequency's new doses have no hour on the sheet yet (the caregiver picks it); keyon
 * needs one, so they take standard hours until the caregiver moves them.
 */
import { orderNumberOf as recordOrderNumber, isNonPrescription as isNonPrescriptionRecord } from '@/utils/nonPrescription';

const FORM_FREQUENCY = {
  daily: '1 time daily',
  'after every meal': 'after breakfast, lunch, dinner',
  '1 hour after every meal': 'after breakfast, lunch, dinner',
};

/**
 * Pre-pour's frequency in the medication form's words: the form lists "2 time daily" (not "2 times daily")
 * and blanks a Frequency it does not list, which leaves the nursing edit form unable to save.
 */
export function formFrequency(freq) {
  const f = String(freq || '').trim();
  if (FORM_FREQUENCY[f]) return FORM_FREQUENCY[f];
  const m = /^(\d+)\s*times?\s*daily$/i.exec(f);
  return m ? `${m[1]} time daily` : f;
}

/** "8am" → "08:00"; an HH:MM passes through; '' for anything else. */
export function to24(h) {
  const s = String(h || '').trim();
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 5);
  const m = /^(\d{1,2})(am|pm)$/i.exec(s);
  if (!m) return '';
  let n = Number(m[1]) % 12;
  if (/pm/i.test(m[2])) n += 12;
  return String(n).padStart(2, '0') + ':00';
}

/** "20:00" → "8pm", "09:30" → "9:30am" (the sheet's hour words). */
export function toSheetHour(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || '').trim());
  if (!m) return '';
  const n = Number(m[1]);
  const h12 = n % 12 === 0 ? 12 : n % 12;
  return h12 + (m[2] === '00' ? '' : ':' + m[2]) + (n < 12 ? 'am' : 'pm');
}

/** The schedule keyon keeps for a medication (medicationlog.yearmedtime), as HH:MM ascending. */
export function keyonTimesOf(record) {
  let slots = [];
  try { slots = JSON.parse((record && record.yearmedtime) || '[]'); } catch (e) { slots = []; }
  return [...new Set((Array.isArray(slots) ? slots : []).map((s) => to24(s && s.time)).filter(Boolean))].sort();
}

// the usual hours for N doses a day, then any hour still free
const STANDARD_HOURS = { 1: ['08:00'], 2: ['08:00', '20:00'], 3: ['08:00', '14:00', '20:00'], 4: ['08:00', '12:00', '16:00', '20:00'] };
const ANY_HOURS = ['08:00', '20:00', '14:00', '12:00', '16:00', '22:00', '06:00', '10:00', '18:00'];
/**
 * `count` times: the usual hours for that many doses, each kept time in place of the usual hour nearest to it, so the
 * doses stay spread over the day (a 7am dose raised to twice daily is 07:00 and 20:00, not 07:00 and 08:00). More kept
 * times than `count`: the earliest. A count with no usual hours: the kept times, then any hour still free.
 */
export function fillTimes(times, count) {
  const kept = [...new Set(times.map(to24).filter(Boolean))].sort();
  if (kept.length >= count) return kept.slice(0, Math.max(0, count));
  const usual = STANDARD_HOURS[count];
  if (usual) {
    const slots = [...usual];
    const taken = new Set();
    const mins = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
    kept.forEach((t) => {
      let best = -1;
      slots.forEach((h, i) => {
        if (!taken.has(i) && (best < 0 || Math.abs(mins(h) - mins(t)) < Math.abs(mins(slots[best]) - mins(t)))) best = i;
      });
      taken.add(best);
      slots[best] = t;
    });
    return slots.sort();
  }
  const out = [...kept];
  for (const h of ANY_HOURS) {
    if (out.length >= count) break;
    if (!out.includes(h)) out.push(h);
  }
  return out.sort();
}

// keyon's rule (utils/nonPrescription.js): no order means non-prescription. This also took the flag, which keyon
// does not read (review of #336, 2026-10-10).
function orderNumberOf(m) { return recordOrderNumber(m.keyon); }
function isNonPrescription(m) { return isNonPrescriptionRecord(m.keyon); }
function ymdOf(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function hmsOf(d) { return [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':'); }
function slots(times, dose) { return times.map((t) => ({ time: to24(t), status: 'pending', dosage: Number(dose) || 1 })); }

/** HoldMedication: `hold` is { start, end, times: sheet hours or null for all, reason }. Both days always go. */
export function holdRequest(ctx, m, hold, now) {
  const times = hold.times && hold.times.length ? hold.times.map((t) => to24(t) + ':00') : null;
  return {
    accountnumber: ctx.account, npinumber: ctx.actorId, patientid: ctx.patient, provsubaccnt: ctx.actorId,
    orderDate: ymdOf(now), orderTime: hmsOf(now),
    holdobjec: {
      medname: m.name, medentryid: Number(m.id), dateRange: [hold.start, hold.end || hold.start], times,
      reason: hold.reason, type: times ? 'specific' : 'all', ordernumber: orderNumberOf(m), status: 'hold', archiveHLDDC: {},
    },
  };
}

/** ClearCurrentMedicationHold - nursing's spelling here is provsubaccount (HoldMedication's is provsubaccnt). */
export function releaseRequest(ctx, m, reason) {
  return {
    accountnumber: ctx.account, npinumber: ctx.actorId, patientid: ctx.patient, provsubaccount: ctx.actorId,
    ordernumber: orderNumberOf(m), medentryid: Number(m.id), medname: m.name, reason: reason || '',
  };
}

/**
 * ChangeOrderForMedLogTimes: `change` is { dose, freq (pre-pour's words), times (HH:MM or sheet hours), reason }.
 * A prescription becomes a change order for the prescriber to sign, with the order's details as they are; a
 * medication with no order is changed at once by keyon and needs only the change.
 */
export function changeRequest(ctx, m, change, now) {
  const base = {
    pid: ctx.patient, accountId: ctx.account, provsubaccount: ctx.actorId, medentryid: Number(m.id), medname: m.name,
    ordernumber: orderNumberOf(m), status: 'Change', changeorder: true, changereason: change.reason, adminDate: ymdOf(now),
    dosage: String(change.dose), frequency: formFrequency(change.freq), slotedtimes: slots(change.times, change.dose),
  };
  if (isNonPrescription(m)) return { ...base, providerid: ctx.actorId };
  const r = m.keyon || {};
  return {
    ...base,
    providerid: '123456789', // what Medication Administration sends; keyon takes the prescriber from providerNpi
    diagnosis: r.diagnose_code || r.diagnosis || '', route: r.route || '', prn: String(r.prn) === '1',
    unitType: r.med_doseuom || '', ndcnumber: r.ndcnumber || '', rxnorns: r.rxnorns || '', quantity: r.total,
    medholdstartdate: '', medholdenddate: '',
    providerName: r.primary_physician || r.providername || '', providerNpi: r.providernpi || r.provnpi || '',
    providerDea: r.providerdeanumber || '', providerAddress: r.provideraddress || '', providerOffice: r.provideroffice || '',
    providerCell: r.providercell || '', providerEmail: r.provideremail || '', previousProvider: r.primary_physician || '',
    pharmacyName: r.pharmacyname || '', pharmacyNpi: r.pharmacyNpi || '', pharmacyDea: r.pharmdea || '',
    pharmacyAddress: r.pharmaddress || '', pharmacyPhone: r.pharmtel || '', pharmacyCell: r.pharmcell || '',
    pharmacyEmail: r.pharmemail || '',
    orderDate: ymdOf(now), orderTime: hmsOf(now),
    rxNumber: r.rxnumber || '', filledDate: r.dtfilled || '', refills: r.refills || '',
    startDate: r.med_startdate || '', endDate: r.med_enddate || '',
    refillReminderDate: r.refilreminderdt || '', expirationDate: r.refillexpirationdt || '',
  };
}

/** InsertUpdateMedLogTimes, nursing's "No, just update times": `move` is { times: the sheet's hours after the move, reason }. */
export function moveRequest(ctx, m, move, now) {
  return {
    pid: ctx.patient, accountId: ctx.account, providerid: ctx.actorId, provsubaccount: ctx.actorId, medentryid: Number(m.id),
    // the medication's own status, as Medication Administration sends it: keyon writes it into medicationlog, and
    // "Active" here approved a change still waiting on the provider (review of #336, 2026-10-10)
    medname: m.name, ordernumber: orderNumberOf(m), status: (m.keyon || {}).status || 'Active', changeorder: false, changereason: move.reason,
    adminDate: ymdOf(now), slotedtimes: slots(move.times, m.dose),
  };
}

/**
 * GetPrePourHolds → { [medId]: [{ start, end, times, reason, fromKeyon }] } in the sheet's hour words. A hold of
 * every one of the medication's keyon times is an all-times hold (times null), so it covers whatever hour the
 * sheet pours that dose at.
 */
export function holdsFromServer(holds, meds) {
  const schedule = Object.fromEntries((meds || []).map((m) => [String(m.id), keyonTimesOf(m.keyon)]));
  const out = {};
  (holds || []).forEach((h) => {
    const id = String(h.medentryid);
    const sched = schedule[id] || [];
    const times = (h.times || []).map((t) => to24(t));
    const all = sched.length > 0 && sched.every((t) => times.includes(t));
    (out[id] = out[id] || []).push({
      start: h.start, end: h.end, times: all ? null : times.map(toSheetHour), reason: h.reason || '', fromKeyon: true,
    });
  });
  return out;
}

const PAGE_STATUS = { hold: 'Hold', release: 'Active', hold_ended: 'Active', change: 'Change', times: 'Change', completed: 'Completed', discontinue: 'Discontinue' };
/** GetMedStatusEvents → the page's change entries (History), oldest first, filed under the day each was made. */
export function changesFromEvents(events) {
  return (events || []).map((e) => {
    const at = String(e.at || '').replace(' ', 'T');
    return {
      medId: String(e.medentryid), name: e.medname || '', status: PAGE_STATUS[e.status] || e.status, label: e.label || '',
      text: e.text || '', reason: e.reason || '', by: e.actor_name === 'system' ? 'System' : (e.actor_name || ''),
      at, day: at.slice(0, 10),
    };
  });
}
